import React, { useState } from 'react';

export default function AdminAuthenticationSection({ appId }) {
  const [expandedSections, setExpandedSections] = useState({
    stytch: false,
    googleSignIn: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span>🔐</span>
          <span>Authentication</span>
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Authentication service configuration
        </p>
      </div>

      <div className="space-y-4">
        {/* Stytch Configuration */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('stytch')}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h4 className="text-sm font-semibold text-gray-900">Stytch</h4>
            <span className="text-gray-500 text-sm">
              {expandedSections.stytch ? '▼' : '▶'}
            </span>
          </button>
          {expandedSections.stytch && (
            <div className="p-4 pt-4 border-t border-gray-200 bg-gray-50">
              <div className="space-y-4 text-sm text-gray-700">
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Stytch Authentication Setup</h5>
                  <ol className="list-decimal list-inside space-y-3 text-gray-600">
                    <li>
                      <span className="font-medium text-gray-700">Create a project at Stytch</span>
                      <div className="ml-5 mt-1 space-y-1">
                        <p className="text-xs">
                          <span className="font-medium text-indigo-600">Consumer</span> — Individual user accounts (social login, magic links, passwords). Best for apps where each user has their own separate data.
                        </p>
                        <p className="text-xs">
                          <span className="font-medium text-indigo-600">B2B SaaS</span> — Organization-based accounts with team invites, roles, and SSO. Best for apps where users collaborate within a company/team.
                        </p>
                      </div>
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">Enable Frontend SDKs</span>
                      <p className="ml-5 mt-1 text-xs">Add your domains to <span className="font-mono bg-gray-200 px-1 rounded">Authorized applications</span> in the SDK Configuration settings.</p>
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">Add your callback URL</span>
                      <p className="ml-5 mt-1 text-xs">Add <span className="font-mono bg-gray-200 px-1 rounded">{'{your-domain}'}/authenticate</span> to <span className="font-mono bg-gray-200 px-1 rounded">Redirect URLs</span>.</p>
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">Set session duration</span>
                      <p className="ml-5 mt-1 text-xs">Under <span className="font-mono bg-gray-200 px-1 rounded">Session settings</span>, set max session duration (e.g., 10080 minutes for 7 days).</p>
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">Copy your Public token</span>
                      <p className="ml-5 mt-1 text-xs">From <span className="font-mono bg-gray-200 px-1 rounded">API Keys</span>, copy the Public token to your app's environment variables when the Stytch code is added to your app.</p>
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">Store your project IDs</span>
                      <p className="ml-5 mt-1 text-xs">The project ID is needed when adding Stytch to the backend. If not yet added as environment variables, save both your "Test" and "Production" project IDs in the Private Notes section. These projects can be accessed via <span className="font-mono bg-gray-200 px-1 rounded">https://stytch.com/dashboard?project_id=...</span></p>
                    </li>
                  </ol>
                </div>
                <div>
                  <a
                    href="https://stytch.com/dashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-zapt-purple rounded-lg hover:bg-zapt-purple/90 transition-colors"
                  >
                    Open Stytch Dashboard →
                  </a>
                </div>

                {/* Google Sign-in Inner Dropdown */}
                <div className="border border-gray-300 rounded-lg mt-4">
                  <button
                    onClick={() => toggleSection('googleSignIn')}
                    className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-100 transition-colors rounded-lg"
                  >
                    <h5 className="text-sm font-semibold text-gray-900">Google Sign-in Summary</h5>
                    <span className="text-gray-500 text-sm">
                      {expandedSections.googleSignIn ? '▼' : '▶'}
                    </span>
                  </button>
                  {expandedSections.googleSignIn && (
                    <div className="p-3 pt-0 border-t border-gray-300 bg-white rounded-b-lg">
                      <div className="space-y-3 text-sm text-gray-700">
                        <p className="text-gray-600">
                          <span className="font-semibold text-gray-900">Frontend only</span> — Uses existing Stytch headless imports (<code className="font-mono bg-gray-200 px-1 rounded text-xs">useStytch</code>, <code className="font-mono bg-gray-200 px-1 rounded text-xs">useStytchSession</code> from <code className="font-mono bg-gray-200 px-1 rounded text-xs">@stytch/react</code>); added a "Continue with Google" button that calls <code className="font-mono bg-gray-200 px-1 rounded text-xs">stytch.oauth.google.start()</code> with redirect URLs; the same token handler checks for <code className="font-mono bg-gray-200 px-1 rounded text-xs">stytch_token_type=oauth</code> and calls <code className="font-mono bg-gray-200 px-1 rounded text-xs">stytch.oauth.authenticate()</code> to complete login. No backend changes needed — Stytch handles the OAuth flow, and the session cookie works the same as magic link authentication.
                        </p>
                        <div>
                          <p className="font-semibold text-gray-900 mb-2">Code:</p>
                          <pre className="bg-gray-800 text-gray-100 p-3 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
{`// Import (already in place)
import { useStytch, useStytchSession } from '@stytch/react';

// Button handler
const handleGoogleOAuth = async () => {
  await stytch.oauth.google.start({
    login_redirect_url: \`\${window.location.origin}/login\`,
    signup_redirect_url: \`\${window.location.origin}/login\`,
  });
};

// Token authentication (handles both magic link and OAuth)
const token = searchParams.get('token');
const stytchTokenType = searchParams.get('stytch_token_type');

if (stytchTokenType === 'oauth') {
  await stytch.oauth.authenticate(token, {
    session_duration_minutes: 60 * 24 * 7,
  });
}`}
                          </pre>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

